import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { saveTemplate } from "../../utils/templateStorage";
import { ReportFormData } from "../../types/ReportFormData";
import { getTranslatedError } from "../../utils/translateError";

interface SaveTemplateModalProps {
	isOpen: boolean;
	onClose: () => void;
	formData: ReportFormData;
	onSave: () => void;
}

const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
	isOpen,
	onClose,
	formData,
	onSave,
}) => {
	const { t } = useTranslation();
	const [templateName, setTemplateName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const handleSave = async () => {
		if (!templateName.trim()) {
			setError(t("errors.templateNameRequired"));
			return;
		}

		setError(null);
		setIsSaving(true);

		try {
			await saveTemplate(templateName.trim(), formData);
			setTemplateName("");
			onSave();
			onClose();
		} catch (err) {
			setError(getTranslatedError(err, "errors.saveTemplateFailed"));
		} finally {
			setIsSaving(false);
		}
	};

	const handleClose = () => {
		setTemplateName("");
		setError(null);
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} title={t("form.saveTemplate.title")}>
			<div className="space-y-4">
				<p className="text-gray-600">{t("form.saveTemplate.description")}</p>

				<Input
					label={t("form.saveTemplate.templateName")}
					value={templateName}
					onChange={(e) => {
						setTemplateName(e.target.value);
						setError(null);
					}}
					placeholder={t("form.saveTemplate.templateNamePlaceholder")}
					error={error || undefined}
					autoFocus
					onKeyDown={(e) => {
						if (e.key === "Enter" && !isSaving) {
							handleSave();
						}
					}}
				/>

				{error && (
					<div className="p-3 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 rounded-lg shadow-sm">
						<div className="flex items-center">
							<svg
								className="w-4 h-4 mr-2 flex-shrink-0"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="font-semibold text-sm">{error}</span>
						</div>
					</div>
				)}

				<div className="flex justify-end gap-3 pt-4">
					<Button variant="secondary" onClick={handleClose} disabled={isSaving}>
						{t("common.cancel")}
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={isSaving || !templateName.trim()}
					>
						{isSaving
							? t("form.saveTemplate.saving")
							: t("form.saveTemplate.saveButton")}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default SaveTemplateModal;
